import type { ParameterLibraryType } from '@malaberg/shared';
/**
 * LibraryEditor — T26.
 *
 * Shared driver component for all six library tabs. Controlled by props:
 *   type       — which parameter type to manage
 *   canWrite   — whether to show Edit / Add / Archive / Revert controls
 *   perProduct — if true: show product picker at top; scope = product:<id>
 *                if false (awareness_levels): workspace scope, no picker
 *
 * Layout:
 *   - (optional) Product picker
 *   - Two-column: left rail = entry list, right pane = entry detail + history
 *   - "Add new entry" button top-right (canWrite)
 *   - Entry detail: Display name, Edit + Archive buttons (canWrite)
 *   - History expander (EntryHistoryExpander)
 */
import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { librariesApi } from '../../../api/endpoints/libraries';
import { productsApi } from '../../../api/endpoints/products';
import EntryHistoryExpander from './EntryHistoryExpander';

// ─── Types ───────────────────────────────────────────────────────────────────

interface LibraryEntry {
  id: string;
  version: number;
  type: ParameterLibraryType;
  scope: string;
  value: string;
  displayName: string;
  body?: string | null;
  metadata: Record<string, unknown>;
  status: 'active' | 'archived';
  isCurrent: boolean;
  createdAt: string;
  createdBy?: string | null;
}

interface Product {
  id: string;
  name: string;
}

interface LibraryEditorProps {
  type: ParameterLibraryType;
  canWrite: boolean;
  /**
   * When true, shows a product picker and filters entries by product scope.
   * When false (e.g. awareness_level), uses workspace scope directly.
   */
  perProduct: boolean;
}

// ─── Modal form state ─────────────────────────────────────────────────────────

interface EntryForm {
  displayName: string;
  value: string;
  /** Markdown body — injected verbatim into the prompt as a labelled block. */
  body: string;
  metadata: string; // raw JSON string
}

const EMPTY_FORM: EntryForm = {
  displayName: '',
  value: '',
  body: '',
  metadata: '{}',
};

// ─── Value auto-sanitiser ─────────────────────────────────────────────────────

/** Convert any string to lower-snake-case for the `value` field. */
function toSnakeCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function isHttpUrl(str: string): boolean {
  try {
    const url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function LibraryEditor({ type, canWrite, perProduct }: LibraryEditorProps) {
  const isLandingPageUrl = type === 'landing_page_url';
  // ── Product picker state ────────────────────────────────────────────────────
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // ── Entries state ───────────────────────────────────────────────────────────
  const [entries, setEntries] = useState<LibraryEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [entriesError, setEntriesError] = useState<string | null>(null);

  // ── Selected entry ──────────────────────────────────────────────────────────
  const [selectedEntry, setSelectedEntry] = useState<LibraryEntry | null>(null);

  // ── Modal state ─────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<LibraryEntry | null>(null);
  const [form, setForm] = useState<EntryForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ── Load products (perProduct only) ────────────────────────────────────────
  useEffect(() => {
    if (!perProduct) return;
    async function fetchProducts() {
      try {
        const res = (await productsApi.list({ limit: 200 })) as {
          ok: true;
          items: Product[];
        };
        setProducts(res.items ?? []);
      } catch {
        // Non-critical — product picker will show empty
      }
    }
    void fetchProducts();
  }, [perProduct]);

  // ── Compute scope ───────────────────────────────────────────────────────────
  const scope = perProduct
    ? selectedProductId
      ? `product:${selectedProductId}`
      : null
    : 'workspace';

  // ── Load entries ────────────────────────────────────────────────────────────
  async function fetchEntries() {
    if (!scope) return;
    setLoadingEntries(true);
    setEntriesError(null);
    try {
      const res = (await librariesApi.list(type, {
        scope,
        status: 'active',
      })) as {
        ok: true;
        entries?: LibraryEntry[];
        items?: LibraryEntry[];
      };
      setEntries(res.entries ?? res.items ?? []);
    } catch (err) {
      setEntriesError(err instanceof Error ? err.message : 'Failed to load entries.');
    } finally {
      setLoadingEntries(false);
    }
  }

  useEffect(() => {
    void fetchEntries();
    setSelectedEntry(null);
  }, [scope, type]);

  // ── Open modal for "Add new" ────────────────────────────────────────────────
  function openAddModal() {
    setEditingEntry(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  }

  // ── Open modal for "Edit" ──────────────────────────────────────────────────
  function openEditModal(entry: LibraryEntry) {
    setEditingEntry(entry);
    setForm({
      displayName: entry.displayName,
      value: entry.value,
      body: entry.body ?? '',
      metadata: JSON.stringify(entry.metadata ?? {}, null, 2),
    });
    setFormError(null);
    setModalOpen(true);
  }

  // ── Save (create or update) ────────────────────────────────────────────────
  async function handleSave() {
    setFormError(null);

    // Validate required Display name
    if (!form.displayName.trim()) {
      setFormError('Display name is required.');
      return;
    }

    if (isLandingPageUrl && !isHttpUrl(form.displayName.trim())) {
      setFormError('Enter a valid http(s) landing page URL.');
      return;
    }

    // Parse metadata
    let parsedMetadata: Record<string, unknown> = {};
    try {
      parsedMetadata = JSON.parse(form.metadata || '{}') as Record<string, unknown>;
    } catch {
      setFormError('Metadata must be valid JSON.');
      return;
    }

    // Empty body string → clear it (null), trimmed non-empty → store as-is
    const trimmedBody = form.body.trim();
    const bodyForApi = trimmedBody === '' ? null : form.body;

    setSaving(true);
    try {
      if (editingEntry) {
        await librariesApi.update(type, editingEntry.id, {
          displayName: form.displayName.trim(),
          body: bodyForApi,
          metadata: parsedMetadata,
        });
      } else {
        await librariesApi.create(type, {
          scope: scope ?? 'workspace',
          displayName: form.displayName.trim(),
          ...(isLandingPageUrl
            ? {}
            : {
                value: form.value.trim() ? form.value.trim() : toSnakeCase(form.displayName.trim()),
              }),
          ...(bodyForApi !== null ? { body: bodyForApi } : {}),
          metadata: parsedMetadata,
        });
      }
      setModalOpen(false);
      setSelectedEntry(null);
      await fetchEntries();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  // ── Archive ────────────────────────────────────────────────────────────────
  async function handleArchive(entry: LibraryEntry) {
    try {
      await librariesApi.delete(type, entry.id);
      setSelectedEntry(null);
      await fetchEntries();
    } catch {
      // Surface via the entries error
      setEntriesError('Failed to archive entry.');
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Stack gap="md" data-testid="library-editor">
      {/* ── Product picker ─────────────────────────────────────────────────── */}
      {perProduct && (
        <Select
          label="Product"
          placeholder="Select a product…"
          data={products.map((p) => ({ value: p.id, label: p.name }))}
          value={selectedProductId}
          onChange={setSelectedProductId}
          clearable
          data-testid="library-product-picker"
        />
      )}

      {/* ── No product selected placeholder (perProduct only) ─────────────── */}
      {perProduct && !selectedProductId && (
        <Text c="dimmed" size="sm" data-testid="library-no-product-placeholder">
          Select a product to view entries.
        </Text>
      )}

      {/* ── Main two-column layout (when scope is available) ───────────────── */}
      {scope && (
        <>
          <Group justify="flex-end">
            {canWrite && (
              <Button size="xs" onClick={openAddModal} data-testid="library-add-entry-btn">
                Add new entry
              </Button>
            )}
          </Group>

          {entriesError && (
            <Alert color="red" data-testid="library-entries-error">
              {entriesError}
            </Alert>
          )}

          {loadingEntries && (
            <Text c="dimmed" size="sm">
              Loading…
            </Text>
          )}

          {!loadingEntries && entries.length === 0 && !entriesError && (
            <Text c="dimmed" size="sm" data-testid="library-empty-state">
              No entries yet.
            </Text>
          )}

          {!loadingEntries && entries.length > 0 && (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {/* Left rail: entry list */}
              <Stack gap={4}>
                {entries.map((entry) => (
                  <UnstyledButton
                    key={entry.id}
                    onClick={() => {
                      setSelectedEntry(entry);
                    }}
                    p="xs"
                    style={{
                      borderRadius: 4,
                      background:
                        selectedEntry?.id === entry.id ? 'var(--mantine-color-teal-0)' : undefined,
                      border: `1px solid ${
                        selectedEntry?.id === entry.id
                          ? 'var(--mantine-color-teal-3)'
                          : 'var(--mantine-color-gray-2)'
                      }`,
                    }}
                    data-testid={`library-entry-row-${entry.id}`}
                  >
                    <Text size="sm" fw={selectedEntry?.id === entry.id ? 600 : 400}>
                      {entry.displayName}
                    </Text>
                  </UnstyledButton>
                ))}
              </Stack>

              {/* Right pane: entry detail */}
              <Box>
                {!selectedEntry && (
                  <Text c="dimmed" size="sm">
                    Select an entry to view details.
                  </Text>
                )}

                {selectedEntry && (
                  <Stack gap="sm" data-testid="library-entry-detail">
                    <Title order={6}>{selectedEntry.displayName}</Title>
                    {!isLandingPageUrl && (
                      <Text size="xs" c="dimmed" ff="monospace">
                        value: {selectedEntry.value}
                      </Text>
                    )}

                    {!isLandingPageUrl && selectedEntry.body?.trim() ? (
                      <Box
                        p="xs"
                        style={{
                          background: 'var(--mantine-color-gray-0)',
                          border: '1px solid var(--mantine-color-gray-2)',
                          borderRadius: 4,
                          maxHeight: 240,
                          overflow: 'auto',
                        }}
                        data-testid="library-entry-body-preview"
                      >
                        <Text size="xs" c="dimmed" mb={4}>
                          Injected as:{' '}
                          <code>
                            ===== {type.toUpperCase().replace('_', ' ')}:{' '}
                            {selectedEntry.displayName} =====
                          </code>
                        </Text>
                        <Text
                          size="sm"
                          component="pre"
                          ff="monospace"
                          style={{ whiteSpace: 'pre-wrap', margin: 0 }}
                        >
                          {selectedEntry.body}
                        </Text>
                      </Box>
                    ) : !isLandingPageUrl ? (
                      <Text size="xs" c="dimmed" data-testid="library-entry-no-body">
                        No body. The prompt receives only the one-line label.
                      </Text>
                    ) : null}

                    {canWrite && (
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="default"
                          onClick={() => {
                            openEditModal(selectedEntry);
                          }}
                          data-testid="library-edit-btn"
                        >
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          color="red"
                          onClick={() => {
                            void handleArchive(selectedEntry);
                          }}
                          data-testid="library-archive-btn"
                        >
                          Archive
                        </Button>
                      </Group>
                    )}

                    <EntryHistoryExpander
                      entryId={selectedEntry.id}
                      type={type}
                      canWrite={canWrite}
                      onReverted={() => {
                        void fetchEntries();
                      }}
                    />
                  </Stack>
                )}
              </Box>
            </SimpleGrid>
          )}
        </>
      )}

      {/* ── Entry create/edit modal ────────────────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        title={editingEntry ? 'Edit entry' : 'Add new entry'}
        data-testid="library-entry-modal"
      >
        <Stack gap="sm">
          {formError && (
            <Alert color="red" data-testid="entry-modal-error">
              {formError}
            </Alert>
          )}

          <TextInput
            label={isLandingPageUrl ? 'Landing page URL' : 'Display name'}
            required
            value={form.displayName}
            onChange={(e) => {
              const name = e.currentTarget.value;
              setForm((prev) => ({
                ...prev,
                displayName: name,
                // Auto-fill value if not yet edited
                value:
                  prev.value === '' || prev.value === toSnakeCase(prev.displayName)
                    ? toSnakeCase(name)
                    : prev.value,
              }));
            }}
            data-testid="entry-modal-display-name"
          />

          {!isLandingPageUrl && (
            <>
              <TextInput
                label="Value"
                description="Auto-filled from Display name. Lower-case, underscores only."
                value={form.value}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    value: e.currentTarget.value,
                  }));
                }}
                data-testid="entry-modal-value"
              />

              <Textarea
                label="Body (markdown)"
                description={
                  form.body.trim()
                    ? `Injected into the prompt as: ===== ${type.toUpperCase().replace('_', ' ')}: ${form.displayName || '<label>'} =====`
                    : 'Leave empty for label-only. Add markdown describing what this is — scenes, fears, dialogue cues. Injected verbatim into the prompt.'
                }
                value={form.body}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, body: e.currentTarget.value }));
                }}
                autosize
                minRows={6}
                maxRows={20}
                ff="monospace"
                data-testid="entry-modal-body"
              />
            </>
          )}
          {/*
            The `metadata` JSON column lives on for awareness_level entries
            (which seed `{ rank: N }` for sort order) but the operator-facing
            editor no longer surfaces it — body covers every other type's
            descriptive needs and exposing a raw JSON blob in the modal just
            invited footguns. metadata is carried forward unchanged on edit.
          */}

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setModalOpen(false);
              }}
              data-testid="entry-modal-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              loading={saving}
              disabled={saving}
              onClick={() => {
                void handleSave();
              }}
              data-testid="entry-modal-save-btn"
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
